import { getAssignmentTypes } from '@/defaults'
import { Select, SelectContent, SelectItem, SelectTrigger } from '@ttab/elephant-ui'
import { cn } from '@ttab/elephant-ui/utils'
import { Block } from '@ttab/elephant-api/newsdoc'
import { BookmarkIcon, BookmarkPlusIcon, FilePenIcon, FilePlus2Icon } from '@ttab/elephant-ui/icons'
import type { DefaultValueOption } from '@/types/index'
import type { FormProps } from '../Form/Root'
import { useYValue } from '@/modules/yjs/hooks'
import type * as Y from 'yjs'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import type { TranslationKey } from '@/types/i18next.d'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'

export const AssignmentType = ({ assignment, editable = false, readOnly = false, className, onChange }: {
  assignment?: Y.Map<unknown>
  className?: string
  editable?: boolean
  readOnly?: boolean
} & FormProps): JSX.Element => {
  const [assignmentType, setAssignmentType] = useYValue<Block[]>(assignment, 'meta.core/assignment-type')
  const [, setAssignmentVisibility] = useYValue<string>(assignment, 'data.public')
  const { t } = useTranslation('shared')
  const featureFlags = useFeatureFlags(['hasHast'])

  const selectedOptions = getAssignmentTypes().filter((type) => {
    const value = assignmentType?.map ? assignmentType.map((s) => s.value).sort().join('/') : ''
    return type.value === value
  })

  const { className: defaultClassName = '', ...iconProps } = selectedOptions[0]?.iconProps || {}

  const SelectedIcon = getIcon(selectedOptions, editable, readOnly)

  if (!assignment) {
    return <></>
  }

  if (readOnly) {
    return (
      <span
        title={t(`assignmentTypes.${selectedOptions[0]?.value}` as TranslationKey)}
        className='inline-flex items-center justify-center w-fit h-10 px-2'
      >
        {SelectedIcon
          ? (
              <SelectedIcon
                {...selectedOptions[0].iconProps}
                className={cn(defaultClassName, className, editable ? 'text-foreground' : 'text-primary')}
              />
            )
          : t(`assignmentTypes.${selectedOptions[0]?.value}` as TranslationKey)}
      </span>
    )
  }

  return (
    <Select
      disabled={!editable}
      value={selectedOptions[0]?.value}
      onValueChange={(value) => {
        onChange?.(true)

        switch (value) {
          case 'picture/video':
            setAssignmentType([Block.create({
              type: 'core/assignment-type',
              value: 'picture'
            }),
            Block.create({
              type: 'core/assignment-type',
              value: 'video'
            })
            ])
            break
          case 'flash':
          case 'timeless':
            setAssignmentType([Block.create({
              type: 'core/assignment-type',
              value: value
            })])
            setAssignmentVisibility('false')
            break


          default:
            setAssignmentType([Block.create({
              type: 'core/assignment-type',
              value: value
            })])
            setAssignmentVisibility('true')
        }
      }}
    >
      <SelectTrigger className='w-fit pl-2 pr-1 border-0 hover:bg-slate-200 dark:hover:bg-table-focused'>
        {SelectedIcon
          ? (
              <SelectedIcon
                {...iconProps}
                className={cn(defaultClassName, className, editable ? 'text-foreground' : 'text-primary')}
              />
            )
          : t(`assignmentTypes.${selectedOptions[0]?.value}` as TranslationKey)}
      </SelectTrigger>
      <SelectContent>
        {getAssignmentTypes()
          .filter((option) => !(featureFlags.hasHast && option.value === 'flash'))
          .map((option) => (
            <SelectItem value={option.value} key={option.value}>
              <div className='flex flex-row gap-2'>
                {option.icon && <option.icon {...option.iconProps} />}
                {t(`assignmentTypes.${option.value}` as TranslationKey)}
              </div>
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  )
}

function getIcon(selectedOptions: Omit<DefaultValueOption, 'label'>[], editable: boolean, readOnly = false) {
  const value = selectedOptions[0]?.value

  if (value === 'text' && readOnly) {
    return editable ? FilePlus2Icon : FilePenIcon
  }

  if (value === 'timeless' && readOnly) {
    return editable ? BookmarkPlusIcon : BookmarkIcon
  }

  return selectedOptions[0]?.icon
}
